"use client";

import { getEstateCountLabel } from "@/app/portfolio/portfolio-utils";
import { getWatchDogIcon } from "@/components/hlidaci-pes/watchdog-page/watchDogOverviewUtils";
import { HomeSelectionQuickDetails } from "@/components/index/fragments/HomeSelectionQuickDetails";
import { EstateStatusBadge } from "@/components/portfolio/all-estate-overview/PortfolioEstateCard";
import PercentageIndicator from "@/components/portfolio/PercentageIndicator";
import { PortfolioEstateSelectionPanel } from "@/components/portfolio/portfolio-page/PortfolioEstateSelectionPanel";
import { NewEstateDialog } from "@/components/modals/CreateNewEstate";
import { Button } from "@/components/ui/Button";
import { useEstateContext } from "@/context/EstateContext";
import { useLanguage } from "@/context/LanguageContext";
import { useMenuItemsContext } from "@/context/MenuItemsContext";
import { cn } from "@/lib/utils";
import { formatCZK } from "@/utils/MyEstateHelper";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import Link from "next/link";
import type { ReactNode } from "react";
import { useAppContext } from "@/context/AppContext";
import { hasReachedEstateLimit } from "@/utils/membershipLimits";

export function HomePortfolioSummary({
  compact = false,
  headerAction,
}: {
  compact?: boolean;
  headerAction?: ReactNode;
}) {
  const { PROFILE_ITEMS, userInfoGlobal } = useAppContext();
  const { language } = useLanguage();
  const { USER_ESTATE_ITEMS, setIsOpen, setSelectedItem } =
    useMenuItemsContext();
  const {
    allEstateList,
    selectedEstate,
    setSelectedEstate,
    isLoadingEstate,
    analysis,
    portfolioEstimatedValue,
    totalProfit,
  } = useEstateContext();
  const copy = {
    cs: {
      oneProperty: "1 nemovitost",
      propertyCountFew: "{count} nemovitosti",
      propertyCountMany: "{count} nemovitostí",
      quickOverview: "Rychlý přehled",
      noAddress: "Bez adresy",
      estimatedValue: "Odhadovaná hodnota",
      purchaseValue: "Pořizovací hodnota",
      purchasePrice: "Pořizovací cena",
      rentalReturn: "Celkový výnos z pronájmu",
      totalValueIncrease: "Celkové navýšení hodnoty",
      viewFull: "Zobrazit celé portfolio",
      changeMembership: "Změnit členství",
    },
    en: {
      oneProperty: "1 property",
      propertyCountFew: "{count} properties",
      propertyCountMany: "{count} properties",
      quickOverview: "Quick overview",
      noAddress: "No address",
      estimatedValue: "Estimated value",
      purchaseValue: "Acquisition value",
      purchasePrice: "Purchase price",
      rentalReturn: "Total rental return",
      totalValueIncrease: "Total value increase",
      viewFull: "View full portfolio",
      changeMembership: "Change membership",
    },
  }[language];
  const estates = allEstateList ?? [];
  const isEstateLimitReached = hasReachedEstateLimit(
    estates.length,
    userInfoGlobal?.membership,
  );
  const estimatedValue = analysis.avgPriceSell ?? 0;
  const purchasePrice = selectedEstate?.purchasePrice ?? 0;
  const currentValue = estimatedValue > 0 ? estimatedValue : purchasePrice;
  const EstateIcon = getWatchDogIcon(selectedEstate?.estateType ?? "");
  const metrics = selectedEstate
    ? [
        {
          label: estimatedValue > 0 ? copy.estimatedValue : copy.purchaseValue,
          value:
            currentValue > 0 ? (
              <span className="flex flex-col items-start gap-1.5">
                <span>{formatCZK(currentValue)}</span>
                {estimatedValue > 0 && purchasePrice > 0 ? (
                  <span className="inline-flex items-center gap-2">
                    <PercentageIndicator
                      valueNow={estimatedValue}
                      valueBefore={purchasePrice}
                      variant="banner"
                      locale={language === "cs" ? "cs-CZ" : "en-US"}
                    />
                    <span className="text-[10px] font-normal text-softiq-primary/45 dark:text-white/45">
                      {copy.totalValueIncrease}
                    </span>
                  </span>
                ) : null}
              </span>
            ) : (
              "—"
            ),
        },
        ...(estimatedValue > 0
          ? [
              {
                label: copy.purchasePrice,
                value: purchasePrice > 0 ? formatCZK(purchasePrice) : "—",
              },
            ]
          : []),
        {
          label: copy.rentalReturn,
          value: formatCZK(totalProfit),
          tone:
            totalProfit >= 0 ? ("positive" as const) : ("negative" as const),
        },
      ]
    : [];
  const handleOpenCreateEstate = () => {
    setSelectedItem(
      isEstateLimitReached ? PROFILE_ITEMS[0] : USER_ESTATE_ITEMS[0],
    );
    setIsOpen(true);
  };

  return (
    <section
      className={cn(
        "relative z-10",
        compact ? "!p-0" : "!px-4 !py-10 sm:!px-8",
      )}
    >
      <div
        className={cn(
          "app-section-card mx-auto flex w-full flex-col",
          compact ? "max-w-none" : "max-w-6xl",
        )}
      >
        <PortfolioEstateSelectionPanel
          estates={estates}
          estateCountLabel={getEstateCountLabel(estates.length, copy)}
          isLoading={isLoadingEstate}
          selectedEstateId={selectedEstate?.id ?? null}
          currentEstateValue={estimatedValue}
          portfolioEstimatedValue={portfolioEstimatedValue}
          embedded
          onSelectEstate={setSelectedEstate}
          onCreateEstate={handleOpenCreateEstate}
          createActionLabel={
            isEstateLimitReached ? copy.changeMembership : undefined
          }
          emptyContent={
            estates.length === 0 && !isLoadingEstate ? (
              <NewEstateDialog />
            ) : undefined
          }
          headerAction={headerAction}
        />

        {selectedEstate ? (
          <HomeSelectionQuickDetails
            icon={<EstateIcon aria-hidden="true" className="size-5" />}
            eyebrow={copy.quickOverview}
            title={selectedEstate.name}
            badge={<EstateStatusBadge status={selectedEstate.status} />}
            subtitle={selectedEstate.address || copy.noAddress}
            metrics={metrics}
          />
        ) : null}

        <div className="app-section-footer mt-auto">
          <Button asChild variant="default" size="compact">
            <Link
              href="/portfolio"
              data-tour="dashboard-portfolio-details-link"
            >
              {copy.viewFull}
              <ArrowRightIcon aria-hidden="true" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
